import React, { useEffect, useState } from "react";
import { api, rupiah } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Landmark, QrCode, Wallet, ChevronRight } from "lucide-react";

const METHOD_ICON = { va: Landmark, qris: QrCode, ewallet: Wallet, "ewallet-asia": Wallet };

export const PaymentMethodDialog = ({ open, onOpenChange, total, confirming, onConfirm }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [picked, setPicked] = useState(null); // {method, channel, name}

  useEffect(() => {
    if (!open) { setPicked(null); return; }
    setLoading(true);
    setError("");
    api.get("/payments/channels")
      .then((r) => setGroups(r.data.groups || []))
      .catch(() => setError("Gagal memuat metode pembayaran. Coba lagi."))
      .finally(() => setLoading(false));
  }, [open]);

  const feeText = (c) =>
    c.fee ? (c.fee_type === "PERCENT" ? `+${c.fee}%` : `+${rupiah(c.fee)}`) : "Gratis";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="payment-method-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Pilih Metode Pembayaran</DialogTitle>
          <DialogDescription>
            Total tagihan <b className="text-emerald-700">{rupiah(total)}</b>. Pilih cara pembayaran yang Anda inginkan.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
        ) : error ? (
          <p className="py-6 text-center text-sm text-rose-600" data-testid="channels-error">{error}</p>
        ) : groups.length === 0 ? (
          <p className="py-6 text-center text-sm text-stone-500" data-testid="channels-empty">
            Belum ada metode pembayaran aktif. Metode akan muncul setelah akun iPaymu Anda diaktifkan penuh.
          </p>
        ) : (
          <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1">
            {groups.map((g) => {
              const Icon = METHOD_ICON[g.method] || Wallet;
              return (
                <div key={g.method}>
                  <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-stone-400 mb-2">
                    <Icon size={14} /> {g.name}
                  </p>
                  <div className="grid gap-2">
                    {g.channels.map((c) => {
                      const active = picked?.method === g.method && picked?.channel === c.code;
                      return (
                        <button
                          key={c.code}
                          data-testid={`channel-${g.method}-${c.code}`}
                          onClick={() => setPicked({ method: g.method, channel: c.code, name: c.name })}
                          className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all ${
                            active ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-400" : "border-stone-200 hover:border-emerald-300"
                          }`}
                        >
                          {c.logo ? (
                            <img src={c.logo} alt="" className="h-6 w-10 object-contain" />
                          ) : (
                            <span className="h-6 w-10 flex items-center justify-center rounded bg-stone-100"><Icon size={15} className="text-stone-500" /></span>
                          )}
                          <span className="flex-1 text-sm font-medium text-stone-800">{c.name}</span>
                          <span className="text-[11px] text-stone-400">{feeText(c)}</span>
                          <ChevronRight size={16} className={active ? "text-emerald-600" : "text-stone-300"} />
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Button
          data-testid="confirm-payment-method"
          disabled={!picked || confirming}
          onClick={() => onConfirm(picked.method, picked.channel)}
          className="mt-2 w-full py-6 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-base font-semibold"
        >
          {confirming ? <Loader2 className="animate-spin" /> : picked ? `Bayar dengan ${picked.name}` : "Pilih metode"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
